/**
 * ArchivePastMonthsButton — Archivage des achats des mois TERMINÉS.
 *
 * Objectif : isoler les montants du mois en cours en poussant tous les
 * achats datés d'un mois antérieur dans le dossier "Archives".
 *
 * Deux modes de déclenchement :
 *   1. Automatique (au montage de l'onglet Achats, throttled 1×/jour).
 *   2. Manuel (bouton visible admin & manager).
 *
 * Endpoint : POST /api/expenses/archive-past-months (non destructif, réversible)
 */
import React, { useCallback, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarClock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AUTO_KEY = "achats_auto_archive_past_months_last";

/** Auto-trigger the past-months archival at most once per day per user.
 *  Silencieux : jamais de toast, uniquement un log console si besoin. */
export const useAutoArchivePastMonths = (currentUser, onDone) => {
  const [ranOnce, setRanOnce] = React.useState(false);
  React.useEffect(() => {
    // 🗑 Fév 2026 — Auto-archivage désactivé sur demande utilisateur.
    // Les achats des mois passés restent visibles indéfiniment dans les onglets.
    // Le endpoint backend reste actif si un archivage manuel est nécessaire.
    setRanOnce(true);
    if (typeof onDone === "function") { /* no-op — pas d'appel auto */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);
};

const ArchivePastMonthsButton = ({ currentUser, onArchived, compact = false }) => {
  const [busy, setBusy] = useState(false);
  const role = (currentUser?.role || "").toLowerCase();

  const doArchive = useCallback(async () => {
    if (busy) return;
    const ok = window.confirm(
      "Archiver tous les achats des mois terminés ?\n\n" +
      "• Les achats du mois en cours restent visibles.\n" +
      "• Les mois passés seront déplacés dans le dossier Archives (accessible à l'Administrateur).\n" +
      "• Réversible : l'Administrateur peut restaurer un achat individuellement."
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/expenses/archive-past-months`, {
        actor_name: currentUser?.full_name || currentUser?.username || "Utilisateur",
        actor_role: role,
      });
      const count = r?.data?.archived_count || 0;
      if (count === 0) {
        toast.info("Aucun achat des mois passés à archiver — tout est déjà à jour ✅");
      } else {
        toast.success(`📦 ${count} achat${count > 1 ? "s" : ""} des mois passés archivé${count > 1 ? "s" : ""}`);
        if (typeof onArchived === "function") {
          try { onArchived(); } catch (_) { /* noop */ }
        }
        window.dispatchEvent(new CustomEvent("expenses-archived", { detail: { count, kind: "month_end" } }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur pendant l'archivage");
    } finally {
      setBusy(false);
    }
  }, [busy, currentUser, onArchived, role]);

  if (!["admin", "manager"].includes(role)) return null;

  return (
    <Button
      variant="outline"
      onClick={doArchive}
      disabled={busy}
      size={compact ? "sm" : "default"}
      className="border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 hover:text-cyan-200"
      data-testid="archive-past-months-btn"
      title="Archiver tous les achats des mois terminés (isole le mois en cours)"
    >
      {busy ? (
        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
      ) : (
        <CalendarClock className="w-4 h-4 mr-2" />
      )}
      Archiver les mois terminés
    </Button>
  );
};

export default ArchivePastMonthsButton;
